import mqtt from "mqtt";
import {useEffect, useState} from "react";
import {useLocation} from "react-router";
import {v4 as uuidv4} from "uuid";

// AWS IoT Core configuration. The backend (cres-aws-t1) registers:
//   * Custom authorizer named "cres-t1" (terraform/.../iot/main.tf)
//   * Authorizer enforces username === "cres" (handlers/authorizer/rules/iot.py)
//   * IoT topic rule named "cres_web_t1" (terraform/.../iot/main.tf)
//   * IAM policy authorizes `topicfilter/cres_web_t1/v1/*` for subscribe and
//     `topic/cres_web_t1/v1/*` for receive (free-form topic filters allowed).
// Any drift here from those constants and IoT rejects the connection or
// silently denies the subscribe.
const MQTT_AUTHORIZER_NAME = "cres-t1";
const MQTT_USERNAME = "cres";
const TOPIC_PREFIX = "cres_web_t1/v1";

/**
 * Marker the test publisher (cres-aws-t1/scripts/mqtt/test-mqtt-echo.py)
 * tags messages with. Only payloads carrying this kind value light up the
 * NavBar indicator so day-to-day server-pushed traffic (notifications, sync
 * events, etc.) doesn't make the indicator pulse continuously and confuse
 * it with the test signal.
 */
export const MQTT_PROBE_KIND = "cres-mqtt-echo-probe";

export const NOTIFICATIONS_UPDATED_KIND = "notifications-updated";

/** Lightweight preview carried on a notifications-updated push (for toasting). */
export type NotificationPreview = {
    usid: string;
    /** Apps the notification targets; null means ALL applications. */
    applications: string[] | null;
    notificationType: string;
    title: string;
    body: string;
    /** Deep link to act on the notification, when present. */
    url: string;
};


export type MqttReceivedSummary = {
    /** Topic the most recent probe arrived on. null until first probe. */
    lastTopic: string | null;
    /** Decoded body of the most recent probe. */
    lastPayload: unknown;
    /** Wall-clock timestamp the most recent probe arrived. */
    lastReceivedAt: number | null;
    /** Running counter of probe messages received since connect. */
    probeCount: number;
    /** Running counter of ALL messages received since connect (for debug). */
    totalCount: number;
};

const INITIAL_RECEIVED: MqttReceivedSummary = {
    lastTopic: null,
    lastPayload: null,
    lastReceivedAt: null,
    probeCount: 0,
    totalCount: 0,
};

export type MqttClientHandle = {
    /** The underlying mqtt.js client (null until connect resolves). */
    client: any;
    /** Sticky summary of every message that's come in since connect. */
    received: MqttReceivedSummary;
    /**
     * Counter bumped on every notifications-updated push. The bell re-fetches when it changes,
     * rather than the push carrying the list: the server decides what this user may see, and a
     * payload that carried the rows would be a second answer to that question.
     */
    notificationSignal: number;
    /** Preview of the most recent notifications-updated push. */
    latestNotification: NotificationPreview | null;
};

export function useMqttClient(sessionContext: any): MqttClientHandle {
    const [notificationSignal, setNotificationSignal] = useState<number>(0);
    const [latestNotification, setLatestNotification] = useState<NotificationPreview | null>(null);

    const [client, setClient] = useState<any>(undefined);
    const [received, setReceived] = useState<MqttReceivedSummary>(INITIAL_RECEIVED);

    const location = useLocation();

    useEffect(() => {

        const loggedIn = sessionContext.valid() &&
            sessionContext.ttl() <= 0 &&
            location.pathname !== "/logout";

        let c: any = undefined;

        if (client) client.end(true);
        setClient(undefined);
        setReceived(INITIAL_RECEIVED);

        if (loggedIn) {

            try {

                c = mqtt.connect(`wss://${process.env.REACT_APP_MQTT_ENDPOINT}/mqtt`, {
                    username: `${MQTT_USERNAME}?x-amz-customauthorizer-name=${MQTT_AUTHORIZER_NAME}`,
                    password: sessionContext.getAccessToken(),
                    clientId: `web-${uuidv4()}`,
                    // AWS IoT Core only speaks MQTT 3.1.1 and MQTT 5. Pairing
                    // `protocolId: "MQTT"` (3.1.1) with `protocolVersion: 3`
                    // (3.1) is invalid and silently rejected at the MQTT
                    // layer after the WSS handshake — leaving the client
                    // stuck in a connect→close reconnect loop. 4 is the
                    // wire-level version number for 3.1.1 and matches the
                    // "MQTT" id.
                    protocolId: "MQTT",
                    protocolVersion: 4,
                    clean: true,
                });

                // In cres-t1, `getAccountUserUSID()` actually returns the
                // AccountUser USID (Login2.js sets it from
                // response.data.Company.AccountUserUSID). The cres-aws-t1
                // authorizer builds the IAM policy with `user_usid` =
                // AccountUser.USID for exactly this reason.
                const globalTopic = `${TOPIC_PREFIX}/global`;
                const userTopic = `${TOPIC_PREFIX}/user/${sessionContext.getAccountUserUSID()}`;

                c.on("connect", function () {
                    const topics = [globalTopic, userTopic];
                    c.subscribe(topics, function (err: any) {
                        if (err) {
                            console.error(err);
                        }
                    });
                });

                // AWS IoT closes the WebSocket when the access token's
                // disconnectAfterInSeconds (= the JWT's exp - now at connect
                // time) elapses. mqtt.js then fires `reconnect` and would
                // happily reopen the WSS with the (now expired) access
                // token cached in the connect options, get rejected by the
                // authorizer, close, reconnect, forever. We re-check
                // session liveness on every reconnect attempt — using the
                // sessionContext value FRESH rather than the closure-
                // captured `loggedIn` from effect mount time — and
                // force-end if the user has logged out OR the token has
                // expired. `end(true)` skips the DISCONNECT packet (the
                // socket may already be torn down) and stops the
                // auto-reconnect loop.
                c.on("reconnect", function () {
                    const stillLoggedIn =
                        sessionContext.valid() && sessionContext.ttl() <= 0;
                    if (!stillLoggedIn) {
                        c.end(true);
                    }
                });

                // Every subscribed-topic message bumps totalCount, but only
                // messages whose decoded payload carries
                // kind === MQTT_PROBE_KIND bump probeCount + the
                // last{Topic,Payload,ReceivedAt} fields. The NavBar
                // indicator only flashes on probeCount changes, so
                // day-to-day server traffic stays invisible and a real test
                // publish is unambiguous. Containers that want to react to
                // arbitrary messages can attach their own listener via
                // `client.on("message", ...)`.
                c.on("message", function (topic: string, payload: Buffer) {
                    let decoded: unknown = null;
                    const raw = payload?.toString?.() ?? "";
                    try { decoded = JSON.parse(raw); } catch { decoded = raw; }
                    const kind =
                        !!decoded && typeof decoded === "object" ? (decoded as any).kind : null;
                    const isProbe = kind === MQTT_PROBE_KIND;
                    setReceived((prev) => ({
                        lastTopic: isProbe ? topic : prev.lastTopic,
                        lastPayload: isProbe ? decoded : prev.lastPayload,
                        lastReceivedAt: isProbe ? Date.now() : prev.lastReceivedAt,
                        probeCount: prev.probeCount + (isProbe ? 1 : 0),
                        totalCount: prev.totalCount + 1,
                    }));

                    // A NOTIFICATIONS PUSH IS A NUDGE, NOT THE DATA. The bell re-fetches when the
                    // counter moves; the payload only previews what arrived. Trusting the push for
                    // the list would put a second answer beside the server's about what this user
                    // is allowed to see.
                    if (kind === NOTIFICATIONS_UPDATED_KIND) {
                        const pushed = (decoded as any).notification ?? {};
                        const data = pushed.Data ?? pushed.data ?? null;
                        const apps = pushed.Applications ?? pushed.applications;
                        setLatestNotification({
                            usid: pushed.USID ?? pushed.usid ?? "",
                            applications: Array.isArray(apps) ? apps : null,
                            notificationType: pushed.NotificationType ?? pushed.notificationType ?? "",
                            title: pushed.Title ?? pushed.title ?? "",
                            body: pushed.Body ?? pushed.body ?? "",
                            url: pushed.Url ?? (data && typeof data === "object" && data.url ? String(data.url) : ""),
                        });
                        setNotificationSignal(previous => previous + 1);
                    }
                });

                c.on("error", function (err: any) {
                    console.error(err);
                    c.end();
                });

            } catch (e) {
                if (c) c.end(true);
                c = undefined;
            }

            setClient(c);
        }

        return () => {
            if (client) client.end(true);
            setClient(undefined);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionContext, location]);

    return {client, received, notificationSignal, latestNotification};
}
